import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Users, Copy, CheckCircle, Gift, UserPlus, DollarSign, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'

interface ReferredUser {
  id: number
  email: string
  created_at: string
  referred_at: string | null
  is_active: boolean
  has_paid_initial_fee: boolean
}

interface ReferralStats {
  referral_code: string
  total_referrals: number
  active_referrals: number
  paid_referrals: number
  referral_reward_earned: number
  referred_users: ReferredUser[]
}

export const ReferralDashboard = () => {
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const token = useAuthStore(state => state.token)

  useEffect(() => {
    fetchReferralStats()
  }, [])

  const fetchReferralStats = async () => {
    try {
      const response = await fetch('/api/v1/auth/referral/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (error) {
      console.error('Failed to fetch referral stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const copyReferralLink = () => {
    if (!stats?.referral_code) return
    const link = `${window.location.origin}/register?ref=${stats.referral_code}`
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const copyReferralCode = () => {
    if (!stats?.referral_code) return
    navigator.clipboard.writeText(stats.referral_code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Programma Referral</h2>
        <p className="text-muted-foreground">
          Invita altri trader e guadagna ricompense
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale Invitati</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_referrals || 0}</div>
            <p className="text-xs text-muted-foreground">
              Utenti registrati con il tuo codice
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utenti Attivi</CardTitle>
            <UserPlus className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.active_referrals || 0}</div>
            <p className="text-xs text-muted-foreground">
              Account attivi
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ricompense</CardTitle>
            <Gift className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              ${(stats?.referral_reward_earned || 0).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              Crediti guadagnati
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Il Tuo Codice Referral
          </CardTitle>
          <CardDescription>
            Condividi questo codice o link con chi vuoi invitare
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                value={stats?.referral_code || ''}
                readOnly
                className="text-center text-xl font-mono tracking-widest font-bold"
              />
            </div>
            <Button onClick={copyReferralCode} variant="outline">
              {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                value={stats?.referral_code ? `${window.location.origin}/register?ref=${stats.referral_code}` : ''}
                readOnly
                className="text-sm text-muted-foreground"
              />
            </div>
            <Button onClick={copyReferralLink} variant="outline">
              {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            Quando qualcuno si registra usando il tuo codice, verrà automaticamente associato al tuo account.
          </p>
        </CardContent>
      </Card>

      {stats && stats.referred_users.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>I Tuoi Invitati</CardTitle>
            <CardDescription>
              Elenco degli utenti che si sono registrati con il tuo codice
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.referred_users.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div>
                      <p className="font-medium">{user.email}</p>
                      <p className="text-sm text-muted-foreground">
                        Registrato il {formatDate(user.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user.has_paid_initial_fee && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <DollarSign className="w-3 h-3 mr-1" />
                        Pagante
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      user.is_active 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {user.is_active ? 'Attivo' : 'Inattivo'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats && stats.referred_users.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessun invitato ancora</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Condividi il tuo codice referral con altri trader per iniziare a guadagnare ricompense!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
